import { useState, useEffect, useCallback } from "react"
import PubSub from "pubsub-js"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import Spinner from "aldoo-ra/Spinner"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { showMessageDialog } from "aldoo-ra/MessageDialog"
import {
  PlusIcon,
  PencilIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  StarIcon,
} from "@heroicons/react/24/outline"
import { StarIcon as StarSolid } from "@heroicons/react/24/solid"
import { openGoPremium } from "../GoPremiumDialog"
import { ListThemes, CloneTheme, DeleteTheme, SetDefaultTheme } from "../FileShareFeature/api"
import ThemeThumbnail from "./ThemeCard"
import ThemeEditorDialog from "./ThemeEditorDialog"
import { VIRTUAL_DEFAULT_THEME, DEFAULT_THEME_ID } from "./constants"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"

// Account → Themes gallery. Lists the user's share themes plus the read-only
// built-in default and a "Create new" tile. Custom create/edit/clone are
// Pro-gated: free users get the GoPremium dialog instead of the editor.
export default function ThemesPanel() {
  const [themes, setThemes] = useState([])
  const [canCustomize, setCanCustomize] = useState(false)
  // Per-plan cap pulled from the `max-share-themes` feature-lock entry via
  // ListThemes. Defaulted to a sensible ceiling until the response arrives
  // so a flicker on the Create button doesn't accidentally allow extra rows.
  const [maxThemes, setMaxThemes] = useState(50)
  const [loading, setLoading] = useState(true)
  const [editorTheme, setEditorTheme] = useState(null) // null | {} (new) | theme

  const texts = TextBundle({ id: "transfer_theme_editor" })
  const { translateWithLanguage, getLanguage } = Localization()
  const t = (key, data) => translateWithLanguage(key, getLanguage(), data)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await ListThemes().catch(() => null)
    setThemes(res?.themes || [])
    setCanCustomize(!!res?.canCustomize)
    if (Number.isFinite(res?.maxThemes)) setMaxThemes(res.maxThemes)
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Realtime: another client (or device) changed a theme → refetch.
  useEffect(() => {
    const token = PubSub.subscribe("transfer.theme.changed", () => load())
    return () => PubSub.unsubscribe(token)
  }, [load])

  // Gate any customizing action behind the Pro feature.
  const guard = (fn) => (canCustomize ? fn() : openGoPremium())

  const handleCreate = () => guard(() => {
    if (themes.length >= maxThemes) {
      showMessageDialog({
        title: texts?.theme_limit_title,
        message: texts?.theme_limit_msg,
      })
      return
    }
    setEditorTheme({})
  })
  const handleEdit = (theme) => guard(() => setEditorTheme(theme))

  const handleClone = (theme) =>
    guard(async () => {
      const payload = theme?._id && !theme.readOnly
        ? { themeId: theme._id }
        : { theme: theme.content } // clone the virtual default
      const res = await CloneTheme(payload).catch(() => null)
      if (res?.error === "feature_locked") return openGoPremium()
      await load()
    })

  const handleDelete = async (theme) => {
    const isDefault = !!theme.content?.is_default
    const name = theme.content?.name
    const ok = await showPromptDialogAsync({
      title: texts?.delete_theme_title,
      message: isDefault
        ? t(texts?.delete_default_msg_fmt, { name: name || t(texts?.this_theme) })
        : t(texts?.delete_confirm_msg_fmt, { name: name || t(texts?.this_theme_lower) }),
      yesLabel: texts?.delete_btn,
      noLabel: texts?.cancel_btn,
    })
    if (!ok) return
    const res = await DeleteTheme(theme._id).catch(() => null)
    await load()
    if (res?.wasDefault) {
      // Reload already pulled fresh themes — no other doc carries is_default
      // now, so the Account Default option falls back to the System Default
      // look automatically. Surface a one-shot toast so the user knows.
      showMessageDialog({
        title: texts?.account_default_reset_title,
        message: texts?.account_default_reset_msg,
      })
    }
  }

  const handleSetDefault = async (theme) => {
    // Virtual System Default — pass the sentinel id, server clears the
    // is_default flag on every owned theme and the resolver falls back to
    // the built-in look.
    const id = theme?._id === DEFAULT_THEME_ID ? DEFAULT_THEME_ID : theme._id
    await SetDefaultTheme(id).catch(() => null)
    await load()
  }

  // True when none of the user's themes are flagged is_default — the System
  // Default tile is the effective default in that case and shouldn't show
  // the "Set as default" button.
  const systemDefaultActive = !themes.some((t) => t.content?.is_default)

  const onSaved = async () => {
    setEditorTheme(null)
    await load()
  }

  return (
    <div className="bg-paper rounded-2xl border border-bordered p-6 flex flex-col gap-4">
      <div>
        <Typography variant="h6" className="text-text">{texts?.panel_title}</Typography>
        <Typography variant="caption" className="text-text_secondary">
          {texts?.panel_subtitle}
        </Typography>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Spinner variant="site-loading" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Create-new tile — the empty "+" acts as the button */}
          <button
            type="button"
            onClick={handleCreate}
            className="w-full aspect-video rounded-xl border-2 border-dashed border-bordered hover:border-primary text-text_secondary hover:text-primary flex flex-col items-center justify-center gap-2 transition-colors"
          >
            <PlusIcon className="w-8 h-8" />
            <span className="text-sm font-medium">{texts?.create_theme_btn}</span>
          </button>

          {/* Built-in default — read-only, no clone. Shows is_default state
              + the Set-as-default button (clears any owned default). */}
          <ThemeTile
            theme={VIRTUAL_DEFAULT_THEME}
            readOnly
            badge={texts?.badge_builtin}
            isDefault={systemDefaultActive}
            onSetDefault={!systemDefaultActive ? () => handleSetDefault(VIRTUAL_DEFAULT_THEME) : undefined}
            texts={texts}
          />

          {/* Owned themes */}
          {themes.map((theme) => (
            <ThemeTile
              key={theme._id}
              theme={theme}
              isDefault={!!theme.content?.is_default}
              onEdit={() => handleEdit(theme)}
              onClone={() => handleClone(theme)}
              onDelete={() => handleDelete(theme)}
              onSetDefault={() => handleSetDefault(theme)}
              texts={texts}
            />
          ))}
        </div>
      )}

      {editorTheme !== null ? (
        <ThemeEditorDialog
          theme={editorTheme._id ? editorTheme : null}
          onClose={() => setEditorTheme(null)}
          onSaved={onSaved}
        />
      ) : null}
    </div>
  )
}

function ThemeTile({ theme, readOnly, isDefault, badge, onEdit, onClone, onDelete, onSetDefault, texts }) {
  const name = theme.content?.name || texts?.untitled
  return (
    <div className="group relative aspect-video rounded-xl overflow-hidden border border-bordered">
      {/* Theme preview fills the whole card */}
      <ThemeThumbnail theme={theme} className="!rounded-none !border-0 absolute inset-0 w-full h-full" />

      {/* Name — upper-left corner */}
      <span className="absolute top-2 left-2 max-w-[70%] px-2 py-0.5 rounded-md bg-black/55 text-white text-xs font-semibold truncate">
        {name}
        {badge ? <span className="ml-1.5 font-normal text-white/70">· {badge}</span> : null}
      </span>

      {/* Actions — upper-right corner, on hover */}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!readOnly && onEdit ? (
          <IconBtn title={texts?.edit_btn} onClick={onEdit}><PencilIcon className="w-4 h-4" /></IconBtn>
        ) : null}
        {onClone ? (
          <button
            type="button"
            title={texts?.clone_btn}
            onClick={onClone}
            className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-black/40 text-white text-xs font-medium hover:bg-primary transition-colors"
          >
            <DocumentDuplicateIcon className="w-4 h-4" /> {texts?.clone_btn}
          </button>
        ) : null}
        {!readOnly && onDelete ? (
          <IconBtn title={texts?.delete_btn} onClick={onDelete} danger><TrashIcon className="w-4 h-4" /></IconBtn>
        ) : null}
      </div>

      {/* Default state / set-default — lower-left corner inside the card */}
      {isDefault ? (
        <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-primary text-white text-xs font-semibold flex items-center gap-1">
          <StarSolid className="w-3 h-3" /> {texts?.default_badge}
        </span>
      ) : onSetDefault ? (
        <button
          type="button"
          onClick={onSetDefault}
          title={texts?.set_default_btn}
          className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/60 hover:bg-primary text-white text-xs font-medium flex items-center gap-1 transition-colors"
        >
          <StarIcon className="w-3 h-3" /> {texts?.set_default_btn}
        </button>
      ) : null}
    </div>
  )
}

// Icon action button styled for the dark image overlay.
function IconBtn({ title, onClick, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-lg bg-black/40 text-white transition-colors ${
        danger ? "hover:bg-error" : "hover:bg-primary"
      }`}
    >
      {children}
    </button>
  )
}
